// Phase 5 (web revival) — Stream C.
//
// /asset/{id} — full-screen lightbox. The photo is the page: a fixed
// black overlay covers the app shell entirely, chrome (back, date,
// actions, prev/next) floats on gradient scrims and auto-hides after a
// few idle seconds. Everything that used to be a permanent right rail +
// comments strip now lives in a slide-in Info drawer (toggle: `i` or
// the ⓘ button) — metadata, reactions, shares, trash/restore. The
// comments thread was retired 2026-06 (clutter; reactions cover the
// family use-case).
//
// Navigation model:
//   - ← / → move between neighbor assets from the cached asset lists,
//     with `replace: true` so the history stack stays [grid, one entry]
//     and a single Back always returns to the grid.
//   - Esc closes the Info drawer if open, otherwise goes back.
//   - The grid's scroll position is restored via app/scrollMemory; we
//     record every asset shown here so the grid can scroll the last
//     viewed photo into view when the user arrowed far from where they
//     entered.
//
// Single useQuery against /v1/assets/{id} drives every sub-component;
// the WS listener invalidates qk.asset(id) on asset.updated / baked /
// reaction.* / asset.share.*, so a single source-of-truth query
// refreshes the whole UI on any change.

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { Link, useNavigate, useRouter } from '@tanstack/react-router';
import { useSyncExternalStore } from 'react';
import { api, ApiError } from '../api/client';
import { qk } from '../app/queryClient';
import { snapshot, subscribe } from '../app/auth';
import { rememberViewedAsset } from '../app/scrollMemory';
import { getLightboxSource } from '../app/lightboxNav';
import type {
  AssetDetailDTO,
  AssetListResponse,
  PatchAssetRequest,
} from '../api/types';
import { AssetViewer } from '../components/asset/AssetViewer';
import { AssetMetadataPanel } from '../components/asset/AssetMetadataPanel';
import { ReactionsPanel } from '../components/asset/ReactionsPanel';
import { SharesPanel } from '../components/asset/SharesPanel';

function useAuth() {
  return useSyncExternalStore(subscribe, snapshot);
}

const CHROME_IDLE_MS = 2800;

// How close to the end of the loaded list before we pull the next page,
// so forward navigation never dead-ends at a page boundary.
const NEAR_END_PREFETCH = 8;

// Touch-swipe thresholds: a navigation gesture must travel at least this
// far horizontally and complete within this window (a deliberate flick,
// not a slow drag or a vertical scroll).
const SWIPE_MIN_PX = 55;
const SWIPE_MAX_MS = 800;

const EMPTY_LIST: AssetListResponse = {
  scoped_to_user_id: '',
  assets: [],
  next_cursor: null,
};

// Show chrome, then fade it out after a few idle seconds. `locked`
// (info drawer open) pins it visible.
function useAutoHideChrome(locked: boolean) {
  const [visible, setVisible] = useState(true);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lockedRef = useRef(locked);

  const poke = useCallback(() => {
    setVisible(true);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (lockedRef.current) return;
    timerRef.current = setTimeout(() => setVisible(false), CHROME_IDLE_MS);
  }, []);

  useEffect(() => {
    lockedRef.current = locked;
    if (locked) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setVisible(true);
    } else {
      poke();
    }
  }, [locked, poke]);

  useEffect(() => {
    poke();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [poke]);

  return { visible, poke };
}

export function AssetDetailPage({ assetID }: { assetID: string }) {
  const auth = useAuth();
  const currentUserID = auth.user?.id ?? null;
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();

  const [infoOpen, setInfoOpen] = useState(false);
  const { visible: chromeVisible, poke } = useAutoHideChrome(infoOpen);

  // Touch devices fire no mousemove, so the idle auto-hide would strand the
  // prev/next arrows off-screen with only a tap to recall them — which read
  // as "there's no forward button". On a coarse (touch) pointer we keep the
  // arrows pinned visible; fine-pointer (desktop) keeps the clean auto-hide.
  const coarsePointer = useMemo(
    () =>
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(pointer: coarse)').matches,
    [],
  );

  const query = useQuery({
    queryKey: qk.asset(assetID),
    queryFn: () => api.get<AssetDetailDTO>(`/v1/assets/${assetID}`),
  });

  // The list the user was browsing when they opened this photo (Library /
  // Shared / an album), captured once on mount. Neighbor navigation keeps
  // this page mounted (param-only change), so the source stays stable
  // across ← / →. Null on deep links → fall back to scanning the cache.
  const [source] = useState(getLightboxSource);

  // Re-run that list's infinite query here. Same queryKey ⇒ we share the
  // grid's already-loaded pages (instant, no refetch) but own our own
  // `fetchNextPage`, so forward navigation can extend past the loaded
  // window instead of dead-ending with no `next`.
  const listQuery = useInfiniteQuery({
    queryKey: source?.queryKey ?? ['__lightbox_no_source__'],
    initialPageParam: source?.initialPageParam ?? null,
    queryFn: source?.queryFn ?? (() => Promise.resolve(EMPTY_LIST)),
    getNextPageParam: source?.getNextPageParam ?? (() => undefined),
    enabled: source != null,
    staleTime: 30_000,
  });

  const sourceIds = useMemo(() => {
    if (!source) return null;
    let assets = (listQuery.data?.pages ?? []).flatMap((p) => p.assets);
    if (source.clientFilter) assets = assets.filter(source.clientFilter);
    return assets.map((a) => a.id);
  }, [source, listQuery.data]);

  // Let the grid scroll this photo into view when the user goes back.
  useEffect(() => {
    rememberViewedAsset(assetID);
  }, [assetID]);

  const goBack = useCallback(() => {
    // Deep links / fresh tabs have nowhere to go back to — land on the
    // library instead of bouncing out of the app.
    if (window.history.length > 1) {
      router.history.back();
    } else {
      void navigate({ to: '/library' });
    }
  }, [router, navigate]);

  // ───────── neighbor navigation ─────────
  // Prefer the registered source list (exact on-screen order, and it
  // paginates forward). Fall back to scanning cached lists for a deep-link
  // open where no source was registered.
  const neighbors = useMemo(() => {
    if (sourceIds) {
      const idx = sourceIds.indexOf(assetID);
      if (idx !== -1) {
        return {
          prev: idx > 0 ? sourceIds[idx - 1]! : null,
          next: idx < sourceIds.length - 1 ? sourceIds[idx + 1]! : null,
        };
      }
    }
    // Library lists first, then the shared-with-me feed, then album
    // asset lists — extractAssetIds rejects anything that isn't an
    // asset-list shape (e.g. the ['album', id] summary entries).
    const matches = [
      ...queryClient.getQueriesData<unknown>({ queryKey: qk.assets() }),
      ...queryClient.getQueriesData<unknown>({ queryKey: qk.feedSharedWithMe() }),
      ...queryClient.getQueriesData<unknown>({ queryKey: ['album'] }),
    ];
    let prev: string | null = null;
    let next: string | null = null;
    for (const [, data] of matches) {
      const ids = extractAssetIds(data);
      if (!ids || ids.length === 0) continue;
      const idx = ids.indexOf(assetID);
      if (idx === -1) continue;
      if (idx > 0) prev = ids[idx - 1] ?? prev;
      if (idx < ids.length - 1) next = ids[idx + 1] ?? next;
      // First list that contains this asset wins; mixing across filters
      // would otherwise produce jumpy back/next behavior.
      break;
    }
    return { prev, next };
  }, [assetID, sourceIds, queryClient]);

  // Pull the next page as the user nears the end of the loaded window, so
  // the forward `>` (and swipe-forward) stays available instead of
  // vanishing at each page boundary. No-op once the server runs dry.
  useEffect(() => {
    if (!source || !sourceIds) return;
    const idx = sourceIds.indexOf(assetID);
    if (idx === -1) return;
    if (
      idx >= sourceIds.length - 1 - NEAR_END_PREFETCH &&
      listQuery.hasNextPage &&
      !listQuery.isFetchingNextPage
    ) {
      void listQuery.fetchNextPage();
    }
  }, [
    assetID,
    source,
    sourceIds,
    listQuery.hasNextPage,
    listQuery.isFetchingNextPage,
    listQuery.fetchNextPage,
  ]);

  const goTo = useCallback(
    (id: string) => {
      // replace, not push — arrowing through 40 photos must not bury
      // the grid 40 entries deep in history.
      void navigate({
        to: '/asset/$assetID',
        params: { assetID: id },
        replace: true,
      });
    },
    [navigate],
  );

  // ───────── touch swipe (mobile prev/next) ─────────
  // The photo viewer owns pan/pinch/double-tap-zoom; we only treat a clean
  // horizontal flick as navigation, and only when NOT zoomed (a drag while
  // zoomed is a pan). `zoomedRef` is fed by AssetViewer's onZoomChange and
  // reset on each asset (the viewer remounts to scale 1 per photo).
  const zoomedRef = useRef(false);
  useEffect(() => {
    zoomedRef.current = false;
  }, [assetID]);
  const swipeStart = useRef<{ x: number; y: number; t: number } | null>(null);

  // Capture-phase so we still see the gesture even though react-zoom-pan-
  // pinch captures the pointer on the inner element. Also pokes the chrome
  // (touch fires no mousemove, so a tap is the only way to recall it).
  const onMediaPointerDown = useCallback(
    (e: React.PointerEvent) => {
      poke();
      // Skip swipe-tracking for mouse (uses arrows/keys) and for gestures
      // starting on a <video> — those are the native scrubber/controls and
      // a horizontal drag there means "seek", not "next photo".
      const onVideo =
        e.target instanceof Element && e.target.closest('video') !== null;
      swipeStart.current =
        e.pointerType === 'mouse' || onVideo
          ? null
          : { x: e.clientX, y: e.clientY, t: e.timeStamp };
    },
    [poke],
  );
  const onMediaPointerUp = useCallback(
    (e: React.PointerEvent) => {
      const s = swipeStart.current;
      swipeStart.current = null;
      if (!s || zoomedRef.current) return;
      const dx = e.clientX - s.x;
      const dy = e.clientY - s.y;
      if (Math.abs(dx) < SWIPE_MIN_PX) return; // too short to be intentional
      if (Math.abs(dx) < Math.abs(dy) * 1.3) return; // too vertical
      if (e.timeStamp - s.t > SWIPE_MAX_MS) return; // too slow → not a flick
      poke();
      // Swipe left → forward (`>`); swipe right → back (`<`).
      if (dx < 0) {
        if (neighbors.next) goTo(neighbors.next);
      } else if (neighbors.prev) {
        goTo(neighbors.prev);
      }
    },
    [neighbors.next, neighbors.prev, goTo, poke],
  );
  const clearSwipe = useCallback(() => {
    swipeStart.current = null;
  }, []);

  const asset = query.data;
  const isOwner =
    currentUserID !== null && asset !== undefined && asset.owner_id === currentUserID;
  const canEdit = isOwner && asset?.kind === 'photo';

  // Quick favorite toggle in the top bar (owner only) — optimistic so
  // the heart doesn't lag the keypress.
  const favMutation = useMutation<
    AssetDetailDTO,
    ApiError,
    boolean,
    { prevAsset?: AssetDetailDTO }
  >({
    mutationFn: (favorite) =>
      api.patch<AssetDetailDTO>(`/v1/assets/${assetID}`, {
        favorite,
      } satisfies PatchAssetRequest),
    onMutate: async (favorite) => {
      await queryClient.cancelQueries({ queryKey: qk.asset(assetID) });
      const prevAsset = queryClient.getQueryData<AssetDetailDTO>(qk.asset(assetID));
      if (prevAsset) {
        queryClient.setQueryData<AssetDetailDTO>(qk.asset(assetID), {
          ...prevAsset,
          favorite,
        });
      }
      return { prevAsset };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prevAsset) queryClient.setQueryData(qk.asset(assetID), ctx.prevAsset);
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(qk.asset(assetID), updated);
    },
  });

  const toggleFavorite = useCallback(() => {
    if (!isOwner || !asset) return;
    favMutation.mutate(!asset.favorite);
  }, [isOwner, asset, favMutation.mutate]);

  // ───────── keyboard ─────────
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Skip while user is typing in a form control.
      const t = e.target as HTMLElement | null;
      if (
        t &&
        (t.tagName === 'INPUT' ||
          t.tagName === 'TEXTAREA' ||
          t.tagName === 'SELECT' ||
          t.isContentEditable)
      ) {
        return;
      }
      if (e.key === 'ArrowLeft' && neighbors.prev) {
        e.preventDefault();
        poke();
        goTo(neighbors.prev);
      } else if (e.key === 'ArrowRight' && neighbors.next) {
        e.preventDefault();
        poke();
        goTo(neighbors.next);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (infoOpen) setInfoOpen(false);
        else goBack();
      } else if (e.key === 'i') {
        e.preventDefault();
        setInfoOpen((o) => !o);
      } else if (e.key === 'f') {
        e.preventDefault();
        poke();
        toggleFavorite();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [neighbors.prev, neighbors.next, infoOpen, goBack, goTo, poke, toggleFavorite]);

  // ───────── loading / error states ─────────
  if (query.isLoading) {
    return (
      <div className="fixed inset-0 z-40 flex items-center justify-center bg-black">
        <div
          aria-label="Loading"
          className="size-6 animate-spin rounded-full border-2 border-neutral-800 border-t-neutral-400"
        />
      </div>
    );
  }

  if (query.isError || !asset) {
    const message =
      query.error instanceof ApiError
        ? query.error.code === 'asset.not_found'
          ? 'This photo could not be found.'
          : query.error.reason
        : 'Failed to load asset.';
    return (
      <div className="fixed inset-0 z-40 flex flex-col items-center justify-center gap-4 bg-black px-6">
        <p className="text-sm text-neutral-300">{message}</p>
        <button
          type="button"
          onClick={goBack}
          className="rounded-full border border-white/15 px-4 py-1.5 text-xs text-neutral-300 transition-colors hover:bg-white/10"
        >
          Go back
        </button>
      </div>
    );
  }

  const chromeClass = chromeVisible
    ? 'opacity-100'
    : 'pointer-events-none opacity-0';
  // Prev/next arrows: pinned on touch, auto-hide with the rest on desktop.
  const navClass =
    coarsePointer || chromeVisible
      ? 'opacity-100'
      : 'pointer-events-none opacity-0';

  return (
    <div className="fixed inset-0 z-40 bg-black">
      {/* Media area — shifts left on large screens when the drawer is
          open; on small screens the drawer overlays instead. Top bar and
          prev/next live inside so they stay centered on the photo. */}
      <div
        className={`absolute inset-y-0 left-0 right-0 transition-[right] duration-300 ${
          infoOpen ? 'lg:right-[340px]' : ''
        } ${chromeVisible ? '' : 'cursor-none'}`}
        onMouseMove={poke}
        // Capture-phase pointer handlers: drive touch swipe-to-navigate and
        // recall the auto-hidden chrome on tap (touch fires no mousemove).
        // Capture so we still see the gesture under react-zoom-pan-pinch's
        // pointer capture on the inner element.
        onPointerDownCapture={onMediaPointerDown}
        onPointerUpCapture={onMediaPointerUp}
        onPointerCancelCapture={clearSwipe}
      >
        <AssetViewer asset={asset} onZoomChange={(z) => (zoomedRef.current = z)} />

        {/* Top scrim + actions */}
        <div
          className={`absolute inset-x-0 top-0 z-20 transition-opacity duration-300 ${chromeClass}`}
        >
          <div className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-black/70 to-transparent" />
          <div className="relative flex items-center justify-between p-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
            <IconButton onClick={goBack} label="Back">
              <path d="M19 12H5" />
              <path d="m12 19-7-7 7-7" />
            </IconButton>
            <div className="pointer-events-none absolute inset-x-0 text-center text-[13px] text-neutral-300">
              {formatShortCapture(asset.capture_time_utc ?? asset.created_at)}
            </div>
            <div className="relative flex items-center gap-1.5">
              {isOwner && (
                <IconButton
                  onClick={toggleFavorite}
                  label={asset.favorite ? 'Unfavorite' : 'Favorite'}
                  pressed={asset.favorite}
                  fill={asset.favorite ? 'oklch(71% 0.19 13)' : 'none'}
                  stroke={asset.favorite ? 'oklch(71% 0.19 13)' : 'currentColor'}
                >
                  <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </IconButton>
              )}
              {canEdit && (
                <Link
                  to="/asset/$assetID/edit"
                  params={{ assetID: asset.id }}
                  aria-label="Edit"
                  title="Edit"
                  className="flex size-9 items-center justify-center rounded-full bg-black/40 text-neutral-200 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
                >
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden
                  >
                    <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                  </svg>
                </Link>
              )}
              <IconButton
                onClick={() => setInfoOpen((o) => !o)}
                label="Info"
                pressed={infoOpen}
              >
                <circle cx="12" cy="12" r="9" />
                <path d="M12 16v-5" />
                <path d="M12 8h.01" />
              </IconButton>
            </div>
          </div>
        </div>

        {/* prev / next — only render when a neighbor exists so the
            arrows aren't sitting around as no-ops. */}
        {neighbors.prev && (
          <button
            type="button"
            onClick={() => goTo(neighbors.prev!)}
            aria-label="Previous photo"
            className={`absolute left-3 top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-neutral-200 backdrop-blur-sm transition-[opacity,background-color] duration-300 hover:bg-black/70 hover:text-white ${navClass}`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
        )}
        {neighbors.next && (
          <button
            type="button"
            onClick={() => goTo(neighbors.next!)}
            aria-label="Next photo"
            className={`absolute right-3 top-1/2 z-10 flex size-10 -translate-y-1/2 items-center justify-center rounded-full bg-black/40 text-neutral-200 backdrop-blur-sm transition-[opacity,background-color] duration-300 hover:bg-black/70 hover:text-white ${navClass}`}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden
            >
              <polyline points="9 18 15 12 9 6" />
            </svg>
          </button>
        )}
      </div>

      {/* Info drawer */}
      <aside
        aria-label="Photo info"
        aria-hidden={!infoOpen}
        className={`absolute inset-y-0 right-0 z-30 flex w-[340px] max-w-[85vw] transform flex-col border-l border-white/10 bg-neutral-950/95 backdrop-blur-md transition-transform duration-300 ${
          infoOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        <div className="flex h-12 shrink-0 items-center justify-between border-b border-white/5 px-4">
          <h2 className="font-display text-[15px] font-medium text-neutral-100">
            Info
          </h2>
          <button
            type="button"
            onClick={() => setInfoOpen(false)}
            aria-label="Close info"
            className="flex size-7 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-white/10 hover:text-white"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.75"
              strokeLinecap="round"
              aria-hidden
            >
              <path d="M18 6 6 18" />
              <path d="m6 6 12 12" />
            </svg>
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto">
          <AssetMetadataPanel asset={asset} isOwner={isOwner} />
          <ReactionsPanel asset={asset} />
          <SharesPanel
            asset={asset}
            isOwner={isOwner}
            currentUserID={currentUserID}
          />
        </div>
      </aside>
    </div>
  );
}

// Round ghost icon button used across the lightbox chrome. Children are
// SVG path elements rendered on a 24×24 stroke grid.
function IconButton({
  onClick,
  label,
  pressed,
  fill = 'none',
  stroke = 'currentColor',
  children,
}: {
  onClick: () => void;
  label: string;
  pressed?: boolean;
  fill?: string;
  stroke?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      className="flex size-9 items-center justify-center rounded-full bg-black/40 text-neutral-200 backdrop-blur-sm transition-colors hover:bg-black/70 hover:text-white"
    >
      <svg
        width="17"
        height="17"
        viewBox="0 0 24 24"
        fill={fill}
        stroke={stroke}
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}

function formatShortCapture(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

// Walks a cached AssetListResponse-shaped value (or its useInfiniteQuery
// `{ pages: [...] }` shape) and returns the flat asset_id list. Returns
// null if the data doesn't look like an asset list — keeps us from
// picking up the wrong query key by accident.
function extractAssetIds(data: unknown): string[] | null {
  if (!data || typeof data !== 'object') return null;
  // Bare AssetListResponse.
  const direct = (data as AssetListResponse).assets;
  if (Array.isArray(direct)) {
    return direct.map((a) => a.id);
  }
  // useInfiniteQuery shape: { pages: AssetListResponse[], pageParams: ... }
  const pages = (data as { pages?: AssetListResponse[] }).pages;
  if (Array.isArray(pages)) {
    const out: string[] = [];
    for (const p of pages) {
      if (p && Array.isArray(p.assets)) {
        for (const a of p.assets) out.push(a.id);
      }
    }
    return out;
  }
  return null;
}
